// src/modules/ComplianceModule.tsx
import React, { useEffect, useState } from 'react';
import { ComplianceDashboard } from './ComplianceDashboard';
import { CqcOperationalCompliance } from './CqcOperationalCompliance';
import { FeedbackModule } from './FeedbackModule';
import { DailyHuddle } from './DailyHuddle';
import { useComplianceManager } from '../hooks/useComplianceManager';
import { seedComplianceData } from '../utils/seedComplianceData';
import { Shield, RefreshCw, LayoutDashboard, AlertCircle, MessageSquare, Calendar } from 'lucide-react';

export function ComplianceModule({ user }: { user: any }) {
  const { incidents, tasks, residents, saveIncidents, saveTasks, saveResidents } = useComplianceManager();
  const [activeTab, setActiveTab] = useState<'dashboard' | 'cqc' | 'feedback'>('dashboard');
  const [showHuddle, setShowHuddle] = useState(false);
  const [seedMessage, setSeedMessage] = useState('');

  useEffect(() => {
    const storedIncidents = localStorage.getItem('rmacs_compliance_incidents');
    if (!storedIncidents) {
      seedComplianceData();
      const freshIncidents = JSON.parse(localStorage.getItem('rmacs_compliance_incidents') || '[]');
      const freshTasks = JSON.parse(localStorage.getItem('rmacs_compliance_tasks') || '[]');
      const freshResidents = JSON.parse(localStorage.getItem('rmacs_compliance_residents') || '[]');
      saveIncidents(freshIncidents);
      saveTasks(freshTasks);
      saveResidents(freshResidents);
    }
  }, []);

  const handleSeed = () => {
    seedComplianceData();
    // Reload fresh data into state without full page refresh
    const freshIncidents = JSON.parse(localStorage.getItem('rmacs_compliance_incidents') || '[]');
    const freshTasks = JSON.parse(localStorage.getItem('rmacs_compliance_tasks') || '[]');
    const freshResidents = JSON.parse(localStorage.getItem('rmacs_compliance_residents') || '[]');
    saveIncidents(freshIncidents);
    saveTasks(freshTasks);
    saveResidents(freshResidents);
    setSeedMessage('✅ Data seeded successfully!');
    setTimeout(() => setSeedMessage(''), 3000);
  };

  const allowedRoles = ['SuperAdmin', 'HomeManager', 'ClinicalLead'];
  if (!user || !allowedRoles.includes(user.role)) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center bg-[#F7F5EE] rounded-2xl p-8">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md text-center border-2 border-red-200">
          <Shield className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-red-700">❌ Access Denied</h2>
          <p className="text-gray-600 mt-2">Compliance dashboard is restricted to authorised governance leads.</p>
          <p className="text-sm text-gray-500 mt-1">Your role: {user?.role || 'Unknown'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl border border-[#E1DCCD] p-6 flex flex-wrap justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-[#123B36] flex items-center gap-2">
            <Shield className="w-6 h-6" /> Compliance & Audit Management
          </h2>
          <p className="text-[#6B7A76]">
            Multi‑agency compliance, incident tracking, and closed‑loop action plans
          </p>
        </div>
        <div className="flex items-center gap-3">
          {seedMessage && (
            <span className="text-sm bg-green-100 text-green-800 px-3 py-1 rounded">{seedMessage}</span>
          )}
          <button
            onClick={() => setShowHuddle(true)}
            className="bg-[#123B36] text-white px-4 py-2 rounded-lg hover:bg-[#0E2C28] flex items-center gap-2 text-sm"
          >
            <Calendar className="w-4 h-4" /> Daily Huddle
          </button>
          <button
            onClick={handleSeed}
            className="bg-[#123B36] text-white px-4 py-2 rounded-lg hover:bg-[#0E2C28] flex items-center gap-2 text-sm"
          >
            <RefreshCw className="w-4 h-4" /> Seed Data
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-[#E1DCCD] pb-2">
        <button
          onClick={() => setActiveTab('dashboard')}
          className={`px-4 py-2 rounded-lg text-sm font-semibold ${activeTab === 'dashboard' ? 'bg-[#123B36] text-white' : 'bg-[#F7F5EE] text-[#123B36]'}`}
        >
          <LayoutDashboard className="w-4 h-4 inline" /> Dashboard
        </button>
        <button
          onClick={() => setActiveTab('cqc')}
          className={`px-4 py-2 rounded-lg text-sm font-semibold ${activeTab === 'cqc' ? 'bg-[#123B36] text-white' : 'bg-[#F7F5EE] text-[#123B36]'}`}
        >
          <AlertCircle className="w-4 h-4 inline" /> CQC Operational Compliance
        </button>
        <button
          onClick={() => setActiveTab('feedback')}
          className={`px-4 py-2 rounded-lg text-sm font-semibold ${activeTab === 'feedback' ? 'bg-[#123B36] text-white' : 'bg-[#F7F5EE] text-[#123B36]'}`}
        >
          <MessageSquare className="w-4 h-4 inline" /> Feedback
        </button>
      </div>

      {activeTab === 'dashboard' && <ComplianceDashboard />}
      {activeTab === 'cqc' && <CqcOperationalCompliance />}
      {activeTab === 'feedback' && <FeedbackModule user={user} />}

      {showHuddle && <DailyHuddle incidents={incidents} tasks={tasks} onClose={() => setShowHuddle(false)} />}
    </div>
  );
}